import * as React from 'react';
import { useSelector } from 'react-redux';

// Utils
import { isNilOrEmpty } from '@src/shared/src/util/general';
import { t } from '@toolkit/util/i18n';
import * as sanitizeHTML from 'sanitize-html';

// Constants
import { ACCOMMODATION_TYPES, ENVIRONMENT } from '@src/shared/src/const/app';
// Actions
import { selectors } from '@src/shared/src';
// Models
import { HotelModel } from '@src/shared/src/models';
// Interfaces
import { IRootState } from '@src/store';
// Components
import HotelFacilities from './HotelFacilities';
import { HotelFaresList } from './HotelFaresList';
import HotelImageGallery from './HotelImageGallery';
import HotelMapDetails from './HotelMapDetails';
import { LoaderHotelDetails } from '@pod/trips/components';
import { HotelCheckinInfo } from './HotelCheckinInfo';
// Styles
import '../styles/HotelDetails.scss';

type Props = {
  hotel: HotelModel;
  hotelRoomCount?: number;
  env: ENVIRONMENT;
  isSelectingHotel?: boolean;
  onSelectHotel: (hotelFareIds: string[]) => void;
};

const HotelDetails: React.FC<Props> = (props) => {
  const { hotel, onSelectHotel, isSelectingHotel, env, hotelRoomCount } = props;
  const searchArrCoor = useSelector((state: IRootState) =>
    selectors.search.searchArrCoor(state.search),
  );
  const isBasketEditable = useSelector((state: IRootState) =>
    selectors.checkout.isBasketEditable(state.checkout),
  );

  const getDetailsImagesMarkup = (hotel: HotelModel) => {
    if (!isNilOrEmpty(hotel.pictureURLs)) {
      return (
        <>
          {hotel.accommodationType === ACCOMMODATION_TYPES.VACATION_RENTAL && (
            <div className="tcp-hotel-details-section">
              <div className="tcp-hotel-details-title">
                {t('HotelDescription.title.airbnbGallery')}
              </div>
              <div className="tcp-hotel-description-text">
                {t('HotelDescription.description.airbnbGallery')}
              </div>
            </div>
          )}
          <HotelImageGallery pictureURLs={hotel.pictureURLs} hotelName={hotel.name} />
        </>
      );
    }
  };

  if (isNilOrEmpty(hotel.fares)) {
    return <LoaderHotelDetails finished={false} />;
  }

  return (
    <div className="tcp-hotel-details">
      {hotel.description && (
        <div className="tcp-hotel-details-section">
          <div className="tcp-hotel-details-title">{t('HotelDescription.label.title')}</div>
          <div
            className="tcp-hotel-details-description"
            dangerouslySetInnerHTML={{ __html: sanitizeHTML(hotel.description) }}
          />
        </div>
      )}
      {getDetailsImagesMarkup(hotel)}

      <div className="tcp-hotel-details-col">
        {hotel.services.length > 0 && (
          <div className="tcp-hotel-details-section">
            <div className="tcp-hotel-details-title">{t('hotel.details.facilities')}</div>
            <HotelFacilities facilities={hotel.services} />
          </div>
        )}
        {hotel.extra && hotel.extra?.checkin && hotel.extra?.checkout && (
          <div className="tcp-hotel-details-section tcp-hotel-details-section-checkin">
            <HotelCheckinInfo extra={hotel.extra} />
          </div>
        )}
      </div>

      <div className="tcp-hotel-details-section">
        <HotelFaresList
          amenities={hotel.amenities}
          accommodationType={hotel.accommodationType}
          onSelectHotel={onSelectHotel}
          isSelectingHotel={isSelectingHotel}
          env={env}
          fares={hotel.fares}
          hotelRoomCount={hotelRoomCount}
          isBasketEditable={isBasketEditable}
        />
      </div>
      <div className="tcp-hotel-details-section">
        <div className="tcp-hotel-details-title">{t('hotel.details.mapTitle')}</div>
        <div className="tcp-hotel-details-location-details">
          <div className="tcp-hotel-details-distance">
            <i className="material-symbols">distance</i>
            {hotel.distance} km
          </div>
          <span>{hotel.address}</span>
        </div>
        <HotelMapDetails hotel={hotel} destCoord={searchArrCoor} />
      </div>
    </div>
  );
};

export default HotelDetails;
